/**
 * ChatComposer.jsx — the bottom composer footer for the chat screen.
 *
 * Owns:
 *   • Attachment preview strip (files chosen but not yet sent)
 *   • Create menu (image / video / sound quick actions)
 *   • Paperclip attach button
 *   • Voice mode toggle
 *   • Auto-growing textarea (⌘/Ctrl+Enter to send, Enter for newline)
 *   • Send button
 *
 * Extracted from chat.jsx to keep the parent under the 700-LOC hygiene
 * threshold flagged repeatedly by the testing agent. All state stays in the
 * parent — this is a presentational component that just wires props to DOM.
 */
import React, { useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CreateMenu } from "@/components/CreateMenu";
import LocationButton from "@/components/LocationButton";
import { detectContentType, contentTypeLabel } from "@/lib/content-intent";
import * as L from "lucide-react";

export default function ChatComposer({
  input,
  setInput,
  attachments,
  removeAttachment,
  onPickFiles,
  onPaste,
  onSend,
  onOpenVoice,
  onCreateImage,
  onCreateVideo,
  onCreateSound,
  busy,
  maxFiles,
  accept,
  onExploreDirections, // fires when user clicks the "3 directions first?" chip
}) {
  const fileRef = useRef(null);

  // Content-writer intent preview: when the composer text names a format
  // (LinkedIn post, blog, API doc, privacy policy, etc.), show a subtle
  // chip inviting the user into Copy Studio's 3-directions flow first.
  const intentType = detectContentType(input);

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    // Auto-grow: shadcn Textarea is a native <textarea>, which never resizes
    // with content unless we compute height from scrollHeight. Cap at 160px.
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  };

  const handleKeyDown = (e) => {
    // Enter = newline (default). ⌘/Ctrl+Enter = send. Shift+Enter also stays a
    // newline for muscle-memory. Ignore while IME composition is active so
    // Japanese/Chinese/Korean input doesn't submit early.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="p-3 space-y-2">
      {intentType && onExploreDirections && (
        <button
          type="button"
          onClick={() => onExploreDirections(input, intentType)}
          className="w-full inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-left bg-gradient-to-r from-[var(--rammy-cyan)]/12 to-amber-100/40 hover:from-[var(--rammy-cyan)]/20 hover:to-amber-200/60 border border-[var(--rammy-cyan)]/30 transition group"
          data-testid="chat-explore-directions"
        >
          <span className="w-6 h-6 rounded-full bg-[var(--rm-ink-900)] text-white flex items-center justify-center shrink-0">
            <L.Compass size={12}/>
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
              Copy Studio · Phase 3
            </span>
            <span className="block text-[12px] text-[var(--rammy-text)] font-medium truncate">
              Detected — {contentTypeLabel(intentType)}. Explore 3 strategic angles first?
            </span>
          </span>
          <L.ArrowRight size={13} className="text-[var(--rammy-text-muted)] group-hover:text-[var(--rammy-text)] transition"/>
        </button>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="chat-attach-preview">
          {attachments.map((a, i) => (
            <div key={i} className="relative group" data-testid={`chat-attach-${i}`}>
              {a.kind === "image" ? (
                <img src={a.preview} alt={a.name} className="h-16 w-16 object-cover rounded-xl border border-[var(--rammy-border)]"/>
              ) : (
                <div
                  className="h-16 min-w-[128px] px-3 flex items-center gap-2 rounded-xl border border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)]/60"
                  title={a.name}
                >
                  <L.FileText size={16} className="text-[var(--rammy-cyan-dark)] shrink-0"/>
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium truncate max-w-[140px]">{a.name}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[var(--rammy-text-muted)]">
                      {a.docKind} · {(a.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                </div>
              )}
              <button
                onClick={() => removeAttachment(i)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--rm-ink-900)] text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                aria-label="Remove"
                data-testid={`chat-attach-remove-${i}`}
              >×</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => onPickFiles(e.target.files)}
          data-testid="chat-attach-input"
        />
        <CreateMenu
          onCreateImage={onCreateImage}
          onCreateVideo={onCreateVideo}
          onCreateSound={onCreateSound}
          disabled={busy}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy || attachments.length >= maxFiles}
          className="w-11 h-11 rounded-full border border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)] flex items-center justify-center text-[var(--rammy-text-sec)] disabled:opacity-40"
          aria-label="Attach image"
          data-testid="chat-attach"
        >
          <L.Paperclip size={16}/>
        </button>
        <button
          onClick={onOpenVoice}
          disabled={busy}
          className="w-11 h-11 rounded-full border border-[var(--rammy-cyan)]/60 bg-[var(--rammy-cyan)]/15 hover:bg-[var(--rammy-cyan)]/25 flex items-center justify-center text-[var(--rammy-cyan-dark)] disabled:opacity-40"
          aria-label="Talk to Rammy"
          data-testid="chat-voice"
          title="Talk to Rammy — hands-free"
        >
          <L.Mic size={16}/>
        </button>
        <LocationButton testIdPrefix="chat-location" />
        <Textarea
          value={input}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onPaste={onPaste}
          rows={1}
          placeholder={attachments.length ? "Ask something about the file…  (⌘/Ctrl+Enter to send)" : "Say anything to Rammy…  (⌘/Ctrl+Enter to send)"}
          data-testid="chat-input"
          className="rounded-2xl min-h-11 max-h-40 text-base leading-relaxed resize-none py-2.5 flex-1"
        />
        <Button
          onClick={onSend}
          disabled={busy || (!input.trim() && attachments.length === 0)}
          className="btn-pill btn-deep-teal h-11 px-4 shadow-sm"
          data-testid="chat-send"
          aria-label="Send message"
        >
          <L.Send size={16}/>
        </Button>
      </div>
    </div>
  );
}
